import MoreVertIcon from "@mui/icons-material/MoreVert";
import { disabledBlockSx } from "../disabledStyles";
import { Box, Button, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import { ContentGalleryModal } from "@platformx/authoring-utilities";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getRepeaterValue, setRepeaterValue } from "../../../utils/repeaterPath";
import HiddenTextBox from "../HiddenTextBox/HiddenTextBox";
import "./Query.css";

type QueryProps = {
  field: any;
  state: any;
  errors: any;
  setState: any;
  register: any;
  clearErrors: any;
  isEdit: boolean;
  isRepeater?: boolean;
  isDisabled?: boolean;
};

// When rendered inside a (possibly nested) repeater, field.name is a synthetic
// RHF path (e.g. `entries[0].foo_entries_123`), not a real state key — reads and
// writes must go through field.repeaterPath/childName instead, mirroring the
// coordinate pattern builder.tsx's own handleChange/updateState helpers use.
const getQueryValue = (state: any, field: any, isRepeater: boolean) =>
  isRepeater
    ? getRepeaterValue(state, field?.repeaterPath, field?.childName ?? "")
    : state?.[field?.name];

const setQueryValue = (state: any, setState: any, field: any, isRepeater: boolean, value: any) => {
  if (isRepeater) {
    setState(setRepeaterValue(state, field?.repeaterPath, field?.childName ?? "", value));
    return;
  }
  setState({
    ...state,
    [field?.name]: value,
  });
};

// A saved query value stores its tags as selectedCDPFilter (Content Type/Collection) +
// selectedTags (everything else) - there is no flat `tags` key on it. Flattening both here
// keeps every "does this query have a selection" check consistent with what actually gets
// saved (see handleSelectedContent below).
const getQueryTags = (queryValue: any): string[] => [
  ...(queryValue?.selectedCDPFilter?.length > 0 ? queryValue.selectedCDPFilter : []),
  ...(queryValue?.selectedTags?.length > 0 ? queryValue.selectedTags : []),
];

const Query = (props: QueryProps) => {
  const {
    field,
    errors,
    isEdit,
    state,
    setState,
    register,
    clearErrors,
    isRepeater = false,
    isDisabled = false,
  } = props;
  const { t } = useTranslation();
  const [options, setOptions] = useState("");
  const [openContentType, setContentType] = useState(false);
  const [pickedContent, setPickedContent] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const queryValue = getQueryValue(state, field, isRepeater);
  const queryData =
    isEdit && (getQueryTags(queryValue).length > 0 || queryValue?.searchTerm) ? true : false;

  const open = Boolean(anchorEl);
  const id = open ? "query-popover" : undefined;

  const filterQueryAdd = (filteredNames: any = [], searchTerm1 = "") => {
    if (filteredNames || ([].length > 0 && ("" + searchTerm1).length > 0)) {
      return `${t("categories")} = ${filteredNames.join(", ")} ${t(
        "and_search_terms",
      )} "${searchTerm1}"`;
    } else if (filteredNames || [].length > 0) {
      return `${t("categories")} = ${filteredNames.join(", ")}`;
    } else if (("" + searchTerm1).length > 0) {
      return `${t("search_term")} = "${searchTerm1}"`;
    } else {
      return `${t("all")}`;
    }
  };

  /**
   * open contentType popUp
   */
  const contentTypeOpen = () => {
    setContentType(true);
  };

  /**
   * close contentType popUp
   */
  const onToggleContentGallery = () => {
    setContentType(false);
  };

  const commonProduct = (arr, searchData) => {
    const filteredNames = arr?.map((item) => `"${item}"`);
    const finalData = `${filterQueryAdd(filteredNames, searchData)} ${t("orderByCreateQuery")}`;
    setOptions(finalData);
  };

  const handleSelectedContent = (e) => {
    const tags = getQueryTags(e?.selectedFilter);
    setQueryValue(state, setState, field, isRepeater, e?.selectedFilter);
    setPickedContent(true);
    onToggleContentGallery();
    clearErrors(field?.name);
    commonProduct(tags, e?.selectedFilter?.searchTerm);
  };

  const handleClickListItem = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onDelete = () => {
    setAnchorEl(null);
    setQueryValue(state, setState, field, isRepeater, {}); //clear query
    setPickedContent(false); //enable add new button
  };

  const onEdit = () => {
    setContentType(true);
    handleClose();
  };

  useEffect(() => {
    if (queryData) {
      setPickedContent(true);
      // constract the content
      commonProduct(getQueryTags(queryValue), queryValue?.searchTerm);
    }
  }, [isEdit, queryData]);

  return (
    <Box id={`${field.name}_id`} sx={disabledBlockSx(isDisabled)}>
      {pickedContent ? (
        <Box className='queryBox'>
          <Box component={"span"} className='queryBox_content'>
            {options}
          </Box>
          <IconButton
            onClick={handleClickListItem}
            className='queryBoxOption'
            data-testid='selectoption'>
            <MoreVertIcon />
          </IconButton>
        </Box>
      ) : (
        <Button
          type='button'
          variant='primaryButton'
          className='addnewpage'
          onClick={contentTypeOpen}>
          Choose query
        </Button>
      )}

      {/*  */}
      <HiddenTextBox
        error={errors?.[field?.name]}
        value={queryValue?.filter}
        register={register}
        name={field?.name}
        required={
          field?.validations?.length > 0 &&
          field?.validations?.[0]?.type === "required" &&
          Object.keys(queryValue || {}).length === 0
            ? field?.validations?.[0]?.message
            : ""
        }
      />

      {/* contentType */}
      <ContentGalleryModal
        isDialogOpen={openContentType}
        onClose={onToggleContentGallery}
        handleSelectedContent={handleSelectedContent}
        selectedItem={[]}
        // multi_select={field?.multiple}
        // single_select={!field?.multiple}
        name={field?.name}
        allowed_content={field?.allowed_content}
        allowed_collection={field?.allowed_collection}
        selected_filter={queryValue}
      />

      {/* edit option  */}
      <Menu
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: -5,
        }}>
        <MenuItem sx={{ p: 1 }}>
          <Typography onClick={onEdit} sx={{ padding: "3px 10px 3px 10px" }}>
            {t("edit")}
          </Typography>
        </MenuItem>
        <MenuItem sx={{ p: 1 }}>
          <Typography onClick={onDelete} sx={{ padding: "3px 10px 3px 10px" }}>
            {t("delete")}
          </Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
};
export default Query;
